import { useCallback, useEffect, useState } from "react";
import { CTBRecordsHistory } from "../../helpers/interfaces";
import { fetchCTBRecordsHistory, getJob, getSAPCTBRecords } from "../../apiServices/CTBRecordsApi";
import SAPCTBRecords from "./SAPCTBRecords";
import {
    Button,
    Grid,
    MenuItem,
    Pagination,
    Stack,
    Table,
    TableBody,
    TableHead,
} from "@mui/material";
import {
    CustomTextField,
    StyledTableCell,
    StyledTableRow,
} from "../../layouts/themes";
import {
    formatShortDateString,
    getNameFromTotalEnergiesEmail,
    showAlertMessage,
} from "../../helpers/utils";
import { ArrowBack, Refresh, RemoveRedEye } from "@mui/icons-material";




function CostTrialBalanceHistory() {
    const [costTrialBalanceRecordsHistory, setCostTrialBalanceRecordsHistory] = useState<CTBRecordsHistory[]>([])
    const [records, setRecords] = useState<CTBRecordsHistory[]>([]);
    const [filteredRecords, setFilteredRecords] = useState<CTBRecordsHistory[]>(
        []
    );
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(25);
    const [numOfPages, setNumOfPages] = useState(0);
    const [currentPageRecords, setCurrentPageRecords] = useState<
        CTBRecordsHistory[]
    >([]);
    const [year, setYear] = useState<number>(new Date().getFullYear());
    const [jobID, setJobID] = useState<string | null>(null);
    const [jobStatus, setJobStatus] = useState<string | null>(null);

    const [viewCTBRecords, setViewCTBRecords] = useState(false);
    const [ctbRecordsToView, setCTBRecordsToView] = useState("");
    const [loading, setLoading] = useState(false);

    const getCTBHistory = useCallback(async () => {
        try {
            setLoading(true);
            const data = await fetchCTBRecordsHistory(recordsPerPage, currentPage);
            console.log(data, 'data')
            setCostTrialBalanceRecordsHistory(data.data);
            setCurrentPage(data.pageIndex);
            setNumOfPages(data.totalPages);
            setRecordsPerPage(data.pageSize);
            setCurrentPageRecords(data.data);
        } catch (error) {
            showAlertMessage("⚠️ Failed to load CTB records history.");
        } finally {
            setLoading(false);
        }
    }, [currentPage, recordsPerPage]);
    useEffect(() => {
        getCTBHistory()

    }, [getCTBHistory])
    useEffect(() => {

        const savedJobID = localStorage.getItem("ctbJobID");

        const savedJobStatus = localStorage.getItem("ctbJobStatus");
        const savedJobYear = localStorage.getItem("ctbJobYear");

        if (savedJobYear) setYear(Number(savedJobYear));

        if (savedJobID) setJobID(savedJobID);

        if (savedJobStatus) setJobStatus(savedJobStatus);

    }, []);


    useEffect(() => {

        if (jobID) localStorage.setItem("ctbJobID", jobID);

        if (jobStatus) localStorage.setItem("ctbJobStatus", jobStatus);

        // ✅ Cleanup if job finished

        if (jobStatus === "COMPLETED" || jobStatus === "FAILED") {

            localStorage.removeItem("ctbJobID");

            localStorage.removeItem("ctbJobStatus");
            localStorage.removeItem("ctbJobYear");

        }


    }, [jobID, jobStatus]);



    // 🔄 fetch from SAP
    const getCTBFromSAP = async (year: number) => {
        const data = await getSAPCTBRecords(year);
        setJobID(data.data.id);
        setJobStatus(data.data.status);

        // ✅ Persist job info
        localStorage.setItem("ctbJobID", data.data.id);
        localStorage.setItem("ctbJobStatus", data.data.status);
        localStorage.setItem("ctbJobYear", year.toString());
        showAlertMessage(data.message);
    };

    useEffect(() => {
        if (!jobID) return;

        const checkJob = async () => {
            try {
                const jobData = await getJob(jobID);
                setJobStatus(jobData.status);

                if (jobData.status === "COMPLETED") {
                    showAlertMessage("✅ Records fetched successfully!");
                    clearInterval(interval);
                    const data = await fetchCTBRecordsHistory(recordsPerPage, currentPage);
                    setRecords(data.data);
                }
                else if (jobData === null) {
                    clearInterval(interval);
                    showAlertMessage("❌ Job failed. Please try again.");
                }
                else if (jobData.status === "FAILED") {
                    clearInterval(interval);
                    showAlertMessage("❌ Job failed. Please try again.");
                }
                else if (jobData.status === "INPROGRESS") {
                    console.log("⏳ Job still in progress...");
                }
            } catch (err) {
                clearInterval(interval);
                showAlertMessage("⚠️ Error checking job status. Please try again.");
                localStorage.removeItem("ctbJobID");

                localStorage.removeItem("ctbJobStatus");
                localStorage.removeItem("ctbJobYear");
            }
        };

        // ✅ Run immediately
        checkJob();

        // ✅ Then check every 15 minutes (900,000 ms)
        const interval = setInterval(checkJob, 15 * 60 * 1000);

        return () => clearInterval(interval);
    }, [currentPage, jobID, recordsPerPage]);

    // 🔎 filter records
    useEffect(() => {
        setFilteredRecords(
            records.filter(
                (e) =>
                    searchQuery === "" ||
                    e.fetchedBy.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    e.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    e.year.toString().includes(searchQuery)
            )
        );
        setCurrentPage(1);
    }, [searchQuery, records]);

    // 📄 pagination
    useEffect(() => {
        setNumOfPages(Math.ceil(filteredRecords.length / recordsPerPage));
    }, [recordsPerPage, filteredRecords]);

    useEffect(() => {
        const lastIndex = currentPage * recordsPerPage;
        const firstIndex = lastIndex - recordsPerPage;
        setCurrentPageRecords(filteredRecords.slice(firstIndex, lastIndex));
    }, [currentPage, recordsPerPage, filteredRecords]);

    // 👁 view record details
    const handleEdit = (record: CTBRecordsHistory) => {
        setCTBRecordsToView(record.id);
        setViewCTBRecords(true);
    };

    const handleBack = () => {
        setCTBRecordsToView("");
        setViewCTBRecords(false);
    };

    // ⏩ init state with props
    useEffect(() => {
        const sorted = [...costTrialBalanceRecordsHistory].sort(
            (a, b) => new Date(b.fetchedAt).getTime() - new Date(a.fetchedAt).getTime()
        );
        setRecords(sorted);
    }, [costTrialBalanceRecordsHistory]);

    return (
        <>
            <div className="flex flex-row">
                <div className="flex w-2/3">
                    {viewCTBRecords ? (
                        <Button
                            variant="contained"
                            size="small"
                            color="secondary"
                            onClick={handleBack}
                        >
                            <ArrowBack /> &nbsp;Back
                        </Button>
                    ) : (
                        <CustomTextField
                            size="small"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            variant="outlined"
                            sx={{ width: "50%" }}
                        />
                    )}
                </div>
                <div style={{ flexGrow: 1 }} />
                {!viewCTBRecords && (
                    <>
                        <CustomTextField
                            size="small"
                            placeholder="Enter Year..."
                            value={year}
                            onChange={(e) => setYear(Number(e.target.value))}
                            variant="outlined"
                            sx={{ width: "20%" }}
                        />
                        &nbsp;
                        <div className="flex items-center gap-2">
                            <Button
                                color="secondary"
                                variant="contained"
                                size="small"
                                style={{ textTransform: "none", fontSize: 13 }}
                                onClick={() => getCTBFromSAP(year)}
                                disabled={jobStatus === "INPROGRESS"} // prevent multiple clicks while running
                            >
                                <Refresh /> &nbsp;
                                {jobStatus === "INPROGRESS" ? "Fetching..." : "Fetch CTB Data"}
                            </Button>

                            {/* 🟢 Status Indicator */}
                            {jobStatus && (
                                <div className="flex items-center gap-1 text-sm">
                                    {jobStatus === "INPROGRESS" && (
                                        <>
                                            <span className="text-yellow-600 animate-pulse">⏳ In Progress...</span>
                                        </>
                                    )}
                                    {jobStatus === "COMPLETED" && (
                                        <span className="text-green-600">✅ Completed</span>
                                    )}
                                    {jobStatus === "FAILED" && (
                                        <span className="text-red-600">❌ Failed</span>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>

            <br />

            {!viewCTBRecords && (
                <>
                    <div className="relative overflow-x-auto ">
                        <Table>
                            <TableHead>
                                <StyledTableRow>
                                    <StyledTableCell>SN</StyledTableCell>
                                    <StyledTableCell>Year</StyledTableCell>
                                    <StyledTableCell>Fetched By</StyledTableCell>
                                    <StyledTableCell>Fetched At</StyledTableCell>
                                    <StyledTableCell>Actions</StyledTableCell>
                                </StyledTableRow>
                            </TableHead>
                            <TableBody>
                                {loading && <StyledTableRow>
                                    <StyledTableCell colSpan={5}>
                                        <p style={{ fontSize: "11px", textAlign: "center" }}>Loading...</p>
                                    </StyledTableCell>
                                </StyledTableRow>}
                                {currentPageRecords.map((item, index) => (
                                    <StyledTableRow key={item.id}>
                                        <StyledTableCell>
                                            {index + 1 + (currentPage - 1) * recordsPerPage}
                                        </StyledTableCell>
                                        <StyledTableCell>{item.year}</StyledTableCell>
                                        <StyledTableCell>
                                            {getNameFromTotalEnergiesEmail(item.fetchedBy)}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {formatShortDateString(item.fetchedAt, true)}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            <Button
                                                variant="contained"
                                                color="info"
                                                size="small"
                                                onClick={() => handleEdit(item)}
                                            >
                                                <RemoveRedEye />
                                                &nbsp;View
                                            </Button>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}

                                {currentPageRecords.length === 0 && (
                                    <tr>
                                        <StyledTableCell colSpan={5}>
                                            <p style={{ fontSize: "11px", textAlign: "center" }}>
                                                No record available...
                                            </p>
                                        </StyledTableCell>
                                    </tr>
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    <br />

                    <nav>
                        <div className="flex flex-row">
                            <div className="flex items-start">
                                <div className="px-6 py-2">Rows per page</div>&nbsp;
                                <Grid item xs={12} lg={3}>
                                    <CustomTextField
                                        fullWidth
                                        select
                                        variant="outlined"
                                        size="small"
                                        value={recordsPerPage}
                                        onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                                    >
                                        {[25, 40, 55, 85, 125, 200, 255, 350, 500].map(
                                            (num) => (
                                                <MenuItem key={num} value={num}>
                                                    {num}
                                                </MenuItem>
                                            )
                                        )}
                                    </CustomTextField>
                                </Grid>
                            </div>
                            <div style={{ flexGrow: 1 }} />
                            <Stack spacing={2}>
                                <Pagination
                                    color="secondary"
                                    count={numOfPages}
                                    siblingCount={1}
                                    boundaryCount={1}
                                    onChange={(event, value) => setCurrentPage(value)}
                                    page={currentPage}
                                    size="small"
                                    classes={{ ul: "PaginationUl" }}
                                />
                            </Stack>
                        </div>
                    </nav>
                </>
            )}

            {viewCTBRecords && <SAPCTBRecords ctbRecordsToView={ctbRecordsToView} />}
        </>
    );
}

export default CostTrialBalanceHistory;